import React, {
  useState,
  Fragment,
  useContext,
  useEffect,
  useRef,
} from "react";

import axios from "axios";

import "./CustomerInformationForm.css";

import { store } from "../../../lib/store";
import { nanoid } from "nanoid";
import {
  saveCards,
  getCardsFromStorage,
  getAjaxUrl,
} from "../../../lib/helpers";
import AutoSuggestInput from "../AutoSuggestInput/AutoSuggest";
import { Button, Tooltip, Input, message, Badge } from "antd";
import SelectCardFromDB from "../SelectCardFromDB";

function CustomerInformationForm() {
  const [cards, setCards] = useState(getCardsFromStorage());
  const [validationErrors, setValidationErrors] = useState([]);
  const { dispatch, state } = useContext(store);
  const [sports, setSports] = useState([]);
  const fileImportRef = useRef();

  useEffect(() => {
    axios
      .get(getAjaxUrl() + `?action=mnt_get_categories`)
      .then((res) => {
        if (res.data && res.data.message && res.data.message === "success") {
          setSports(res.data.categories);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  const cardAttributes = {
    year: "Card Year",
    company: "Card Company",
    set_name: "Card Set",
    subset_name: "Card Subset",
    player: "Card Player",
    card_no: "Card Number",
    serial_no: "Card Serial",
    signed: "Card Signed",
    value: "Card Price",
  };

  const updateCard = (value, attrName, cardIndex) => {
    let newCards = cards;
    newCards[cardIndex][attrName] = value.target.value;

    if (attrName === "signed") {
      newCards[cardIndex][attrName] = value.target.checked ? true : false;
    }

    setCards([...newCards]);
  };

  const addNewCard = (defaultCardDetails = {}) => {
    const newCard = {
      initial_id: nanoid(),
      booking_id: "",
      sport_id: "",
      year: "",
      company: "",
      signed: false,
      set_name: "",
      subset_name: "",
      player: "",
      card_no: "",
      serial_no: "",
      value: "",
      created_at: Date.now(),
      grade_points: "",
      updated_at: "",
      grade_autograph: "",
      grade_corners: "",
      grade_center: "",
      grade_edges: "",
      grade_surface: "",
      grade_inner_window: "",
      final_grade: "",
      ...defaultCardDetails,
    };
    setCards([...cards, newCard]);
  };

  const duplicateCard = (idx) => {
    let newCards = cards;
    let duplicatedCard = newCards[idx];

    if (!duplicatedCard) {
      return;
    }

    delete duplicatedCard.initial_id;
    delete duplicatedCard.created_at;

    addNewCard(duplicatedCard);
  };

  const renderCategories = () => {
    return sports.map((sport, sportKey) => {
      return (
        <option key={sportKey} value={sport.slug} id={sport.slug}>
          {sport.name}
        </option>
      );
    });
  };

  const renderCardAttributes = (cardIndex, card) => {
    return Object.keys(cardAttributes).map((attributeKey, indx) => {
      const attribute = cardAttributes[attributeKey];
      let attributeName = attribute.toLowerCase().replace(" ", "_");
      let type =
        attributeName === "card_signed"
          ? "checkbox"
          : "text";
      let cellClass = type === "checkbox" ? "checkbox-opt" : "";
      const failedValidation = validationErrors.find(
        (error) => error.cardIdx === cardIndex && error.valueKey === attribute
      );

      if (
        attributeKey === "player" ||
        attributeKey === "company" ||
        attributeKey === "set_name" ||
        attributeKey === "subset_name" ||
        attributeKey === "year"
      ) {
        return (
          <td
            className={`${cellClass} ${
              failedValidation ? "failedValidation" : ""
            }`}
            key={indx}
          >
            <AutoSuggestInput
              field={attributeKey}
              placeholder={attribute}
              value={cards[cardIndex][attributeKey]}
              setValue={(val) => {
                updateCard({ target: { value: val } }, attributeKey, cardIndex);
              }}
            />
          </td>
        );
      }

      return (
        <td
          className={`${cellClass} ${
            failedValidation ? "failedValidation" : ""
          }`}
          key={indx}
        >
          <Input
            type={type}
            required
            placeholder={attribute}
            value={cards[cardIndex][attributeKey]}
            checked={cards[cardIndex][attributeKey]}
            onChange={(evt) => updateCard(evt, attributeKey, cardIndex)}
          />
        </td>
      );
    });
  };

  const validateCards = () => {
    let validationResult = [];
    cards.forEach((card, cardIdx) => {
      Object.keys(cardAttributes).forEach((valueKey) => {
        const value = card[valueKey];
        if (
          valueKey === "signed" ||
          valueKey === "serial_no"
        ) {
          return;
        }
        if (value === "") {
          validationResult.push({
            cardIdx: cardIdx,
            valueKey: cardAttributes[valueKey],
          });
        }
      });

      if (card["sport_id"] === "") {
        validationResult.push({
          cardIdx: cardIdx,
          valueKey: "sport_id",
        });
      }
    });

    setValidationErrors(validationResult);
    return validationResult;
  };

  const submitOrder = () => {
    dispatch({ type: "setLoadingTrue" });

    const validationErrors = validateCards();
    if (validationErrors.length) {
      console.log("failed");
      dispatch({ type: "setLoadingFalse" });
      return;
    }

    if (!cards.length) {
      dispatch({ type: "setLoadingFalse" });
      return;
    }

    // submit
    const data = new FormData();
    data.append("cards", JSON.stringify(cards));
    data.append("serviceDays", state.serviceType);
    data.append("action", "mnt_add_card_to_cart");

    axios
      .post(window.woocommerce_params.ajax_url, data)
      .then((res) => {
        // disable loading spinner
        dispatch({ type: "setLoadingFalse" });

        const data = res.data;

        if (!data) {
          return;
        }

        if (data.result === "success") {
          saveCards(cards);
          window.location = "/checkout";
        }
      })
      .catch((err) => {
        console.log(err);
        dispatch({ type: "setLoadingFalse" });
      });
  };

  const removeCard = (indx) => {
    let newCardList = cards;
    newCardList.splice(indx, 1);
    setCards([...newCardList]);
  };

  const displayCards = () => {
    return cards.map((card, indx) => {
      const failedValidation = validationErrors.find(
        (error) => error.cardIdx === indx && error.valueKey === "sport_id"
      );

      return (
        <tr key={indx}>
          <td className="cardIndex">
            <div>
              <Tooltip title="Remove card">
                <button
                  aria-label="remove card"
                  onClick={() => {
                    removeCard(indx);
                  }}
                >
                  <i
                    className="x-icon x-icon-trash-o"
                    data-x-icon-o=""
                    aria-hidden="true"
                  ></i>
                </button>
              </Tooltip>
              <Tooltip title="Duplicate card">
                <button
                  aria-label="duplicate card"
                  onClick={() => {
                    duplicateCard(indx);
                  }}
                >
                  <i
                    className="x-icon x-icon-copy"
                    data-x-icon-s=""
                    aria-hidden="true"
                  ></i>
                </button>
              </Tooltip>
              <span>{indx + 1}</span>
            </div>
          </td>
          <td className={`${failedValidation ? "failedValidation" : ""}`}>
            <select
              value={card.sport_id}
              onChange={(evt) => updateCard(evt, "sport_id", indx)}
            >
              <option disabled value="">
                Select A Category
              </option>
              {renderCategories()}
            </select>
          </td>
          {renderCardAttributes(indx, card)}
        </tr>
      );
    });
  };

  const saveToDesktop = (cards) => {
    let file = new Blob([JSON.stringify(cards)], { type: "application/json" });
    let a = document.createElement("a");
    a.href = URL.createObjectURL(file);
    a.download = "savedCards.json";
    a.click();
  };

  const importCards = () => {
    fileImportRef.current.click();
  };

  const validateImport = (data) => {
    const headers =
      "initial_id,booking_id,sport_id,year,company,signed,set_name,subset_name,player,card_no,serial_no,value,created_at,grade_points,updated_at,grade_autograph,grade_corners,grade_center,grade_edges,grade_surface,grade_inner_window,final_grade".split(
        ","
      );
    const validatedData = { data: [], errors: [] };

    validatedData.data = data.filter((row) => {
      let filteredHeaders = Object.keys(row).filter(
        (header) => !headers.includes(header)
      );
      if (filteredHeaders.length) {
        filteredHeaders.map((header) => {
          validatedData.errors.push({
            header,
            message: `Unknown header ${header}`,
          });
        });
        return false;
      }

      return true;
    });

    return validatedData;
  };

  const readURL = (e) => {
    let file = e.target.files[0];
    // Check if the file is an image.
    if (file.type && !file.type.startsWith("application/json")) {
      message.error({
        content: "Unable to read the json file. Please try another file.",
        style: {
          marginTop: "20px",
        },
      });
      return;
    }

    const reader = new FileReader();

    reader.addEventListener("load", (event) => {
      const readData = event.target.result;
      try {
        const jsonData = JSON.parse(readData);

        const validatedFields = validateImport(jsonData);

        if (validatedFields.errors.length) {
          message.error({
            content: `Please note there were some errors we found with your data. See the console for details`,
            style: {
              marginTop: "20px",
            },
          });
          console.log(validatedFields.errors);
        }

        setCards([...cards, ...validatedFields.data]);
      } catch (error) {
        message.error({
          content: `Unable to read the json file. Please try another file. ${error}`,
          style: {
            marginTop: "20px",
          },
        });
      }
    });

    reader.readAsText(file);
  };

  /** Select Card Wizard Related */
  // INFO: GET SETS

  const getCategoryId = (name) => {
    let catId = 0;
    for (let sport of sports) {
      if (sport.name === name) {
        catId = sport.slug;
        break;
      }
    }
    return catId;
  };

  const showCardWizard = (addEmpty = false) => {
    if (addEmpty) addNewCard();
    dispatch({ type: "toggleCardWizard" });
  };
  const nextCardStep = () => {
    let nextStep = state.cardWizardStep + 1;
    switch (nextStep) {
      case 2:
        // fetch sets
        if (state.selectedType) {
          dispatch({ type: "nextStep" });
          let data = new FormData();
          data.append("action", "mnt-label-manager-get-sets");
          data.append("selectedType", state.selectedType);
          axios.post(getAjaxUrl(), data).then((res) => {
            dispatch({
              type: "updateWizardSets",
              value: JSON.parse(res.data.sets),
            });
          });
        }
        break;
      case 3:
        // fetch cards
        if (state.selectedType) {
          dispatch({ type: "nextStep" });
          let data = new FormData();
          data.append("action", "mnt-label-manager-get-cards");
          data.append("selectedSet", JSON.stringify(state.selectedSet));
          data.append("selectedType", state.selectedType);
          axios.post(getAjaxUrl(), data).then((res) => {
            dispatch({
              type: "updateWizardCards",
              value: JSON.parse(res.data.cards),
            });
          });
        }
        break;
      case 4:
        //add card to updated list
        const catId = getCategoryId(state.selectedType);
        if (catId) {
          let serial_number = state.selectedCard.serial_number ? state.selectedCard.serial_number : "";
          serial_number += state.display_serial ? "/" + state.display_serial : "";
          serial_number = serial_number.replace("//", "/");
          const newCard = {
            sport_id: catId,
            year: state.selectedSet.year,
            company: state.selectedSet.brand,
            set_name: state.selectedSet.set_name,
            subset_name: state.selectedSet.subset,
            player: state.selectedCard.card_name,
            card_no: state.selectedCard.card_number,
            serial_no: serial_number,
            created_at: Date.now(),
          };
          addNewCard(newCard);
        }
        dispatch({ type: "nextStep" });
        break;
      default:
        break;
    }
  };

  const previousCardStep = () => {
    dispatch({ type: "previousStep" });
  };

  const onUpdateSet = (key, value) => {
    // console.log(state.selectedSet);
    if (!value || (state.selectedSet && value === state.selectedSet[key])) {
      return;
    }
    dispatch({ type: "updateSelectedWizardSet", payload: { key, value } });
  };

  const onUpdateCard = (key, value) => {
    // console.log(state.selectedCard);
    if (
      //(key !== SERIALNUMBER && !value) ||
      state.selectedCard &&
      value === state.selectedCard[key]
    ) {
      return;
    }
    dispatch({ type: "updateSelectedWizardCard", payload: { key, value } });
  };

  const onUpdateType = (value) => {
    if (!value || value === state.selectedType) {
      return;
    }
    dispatch({ type: "updateSelectedWizardType", value });
  };

  return (
    <Fragment>
      <div style={{ overflow: "scroll", marginBottom: "24px" }}>
        <table className="cardsTable">
          <thead>
            <tr>
              <th>#</th>
              <th>Category</th>
              <th>Set Year</th>
              <th>Company Name</th>
              <th>Set Name</th>
              <th>Subset Name</th>
              <th>Player Name</th>
              <th>Card #</th>
              <th>Serial #</th>
              <th>Autograph</th>
              <th>Declared Value</th>
            </tr>
          </thead>
          <tbody>
            {displayCards()}
            <tr className="newCardRow">
              <td colSpan="12">
                <button
                  onClick={() =>
                    state.serviceType === "2 days"
                      ? cards.length < state.cardsLimit && addNewCard()
                      : addNewCard()
                  }
                >
                  Add Card Manually +
                </button>
                <Button
                  onClick={() =>
                    state.serviceType === "2 days"
                      ? cards.length < state.cardsLimit && showCardWizard()
                      : showCardWizard()
                  }
                  type="dashed"
                  danger
                  style={{
                    fontWeight: "bold",
                    background: "#fbf2a5",
                    padding: 5,
                  }}
                >
                  <span>
                    <Badge
                      count={"New"}
                      style={{ backgroundColor: "#52c41a", marginBottom: 5 }}
                    />{" "}
                    Add Pokemon/Hockey Cards From Our Database +
                  </span>
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <div className="errorMessage">
          {validationErrors.length ? (
            <p>Please ensure all required fields are filled in.</p>
          ) : (
            ""
          )}
        </div>
        {/* <div className='errorMessage'>
          {state.serviceType === '2 days' && <p>Pending quantity for 2 days service: <strong>{state.cardsLimit}</strong></p>}
        </div> */}
        <div className="errorMessage">
          {state.serviceType === "2 days" &&
            cards.length >= state.cardsLimit && (
              <p>We are not able to take more cards in 2 days category.</p>
            )}
        </div>

        <div className="cardsTableButtonsGroup">
          <Button
            onClick={() =>
              state.serviceType === "2 days"
                ? cards.length < state.cardsLimit && addNewCard()
                : addNewCard()
            }
          >
            Add Card Manually
          </Button>
          <input
            type="file"
            accept=".json"
            ref={fileImportRef}
            onChange={readURL}
            style={{ display: "none" }}
          />
          <Button onClick={importCards}>Import Cards</Button>
          <div className="right-div">
            {cards.length ? (
              <Button onClick={() => saveCards(cards)}>Save</Button>
            ) : (
              ""
            )}
            {cards.length ? (
              <Button onClick={() => saveToDesktop(cards)}>
                Save To Desktop
              </Button>
            ) : (
              ""
            )}
            {cards.length ? (
              <Button type="primary" onClick={submitOrder}>
                Proceed To Payment
              </Button>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
      {state.showCardWizard && (
        <SelectCardFromDB
          toggleWizard={showCardWizard}
          nextCardStep={nextCardStep}
          previousCardStep={previousCardStep}
          updateType={onUpdateType}
          updateSet={onUpdateSet}
          updateCard={onUpdateCard}
          step={state.cardWizardStep}
          visible={state.showCardWizard}
          types={state.wizardTypes}
          loading={state.wizardLoading}
          selectedType={state.selectedType}
          years={state.d_years}
          brands={state.d_brands}
          setnames={state.d_setnames}
          subsets={state.d_subsets}
          cardnames={state.d_cardnames}
          cardnumbers={state.d_cardnumbers}
          serialnumbers={state.d_serialnumbers}
          selectedSet={state.selectedSet}
          selectedCard={state.selectedCard}
          displaySerial={state.display_serial}
        />
      )}
    </Fragment>
  );
}

export default CustomerInformationForm;
